import { useEffect, useState } from 'react';
import { Story, Trip } from '../../schema';
import {
  useLazyGetStoriesByTripByTripIdQuery,
  useLazyGetTripsByTripIdQuery,
} from '../../store/backendApi';

interface TripData {
  trip: Trip;
  stories: Story[];
}

interface UseTripDataReturn {
  data: TripData | null;
  loading: boolean;
  error: string | null;
  linkSecret: string | null;
  isApiMode: boolean;
}

const getLinkSecret = (path: string): string | null =>
  path.includes('/trip/_s_') ? path.split('/trip/')[1]?.replaceAll('/', '') || null : null;

/**
 * Hook to load trip data - handles both static (pre-rendered) and API-based loading.
 * Platform-agnostic: Can be used in React Native with minimal modifications.
 *
 * @param pathname - The current route pathname (from React Router or React Navigation)
 * @returns Trip data, loading state, error state, link secret, and mode indicator
 */
export const useTripData = (pathname: string): UseTripDataReturn => {
  const [data, setData] = useState<TripData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const linkSecret = getLinkSecret(pathname);
  const isApiMode = pathname.includes('/p/trip/');

  const [loadTrip] = useLazyGetTripsByTripIdQuery();
  const [loadStories] = useLazyGetStoriesByTripByTripIdQuery();

  useEffect(() => {
    const loadDataViaApi = async () => {
      const tripId = pathname.split('/p/trip/')[1].replaceAll('/', '');
      const [tripResponse, storiesResponse] = await Promise.all([
        loadTrip(tripId),
        loadStories(tripId),
      ]);
      const tripData = tripResponse.data;
      const storiesData = storiesResponse.data;
      if (!tripData || !storiesData) {
        throw new Error('failed to fetch data');
      }
      setData({
        trip: tripData,
        stories: [...storiesData.stories],
      });
    };

    const loadStaticData = async () => {
      const response = await fetch('./data.json');
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const jsonData = await response.json();
      setData(jsonData);
    };

    const loadDataFunc = isApiMode ? loadDataViaApi : loadStaticData;

    (async () => {
      try {
        await loadDataFunc();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    })();
  }, [pathname, isApiMode, loadTrip, loadStories]);

  return {
    data,
    loading,
    error,
    linkSecret,
    isApiMode,
  };
};
